import { Component, ViewEncapsulation, OnInit, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FuseDrawerComponent } from '@fuse/components/drawer';
import { FuseDrawerService } from '@fuse/components/drawer';
import { ApplicationUser, RecentlyPlayedWithUser, UsersFilter } from 'app/core/users/users.types';
import { UsersService } from 'app/core/users/users.service';
import { Member } from 'app/modules/members/members.types';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, tap, finalize } from 'rxjs';
import { PlayerAffiliation, PlayerSelectionResult, PlayerSlot } from 'app/modules/bookings/booking.types';
import { BookingStateService } from '../booking-state.service';

@Component({
    selector: 'add-player-drawer',
    templateUrl: './add-player-drawer.component.html',
    styles: [
        `
            add-player-drawer {
                position: static;
                display: block;
                flex: none;
                width: auto;
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        FuseDrawerComponent,
        MatButtonModule,
        MatTooltipModule,
        MatCardModule,
        MatDividerModule,
        MatChipsModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
    ],
    providers: [
        UsersService,
    ]
})
export class AddPlayerDrawerComponent implements OnInit, OnDestroy {
    searchTerm = '';
    searchResults: ApplicationUser[] = [];
    recentlyPlayedWith: RecentlyPlayedWithUser[] = [];
    isLoading = false;
    isHandlingSearch = false;

    // Store the selected user while waiting for player type selection
    pendingSelectedUser: ApplicationUser | RecentlyPlayedWithUser | null = null;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _searchTerms = new Subject<string>();

    constructor(
        private _usersService: UsersService,
        private _fuseDrawerService: FuseDrawerService,
        private _bookingStateService: BookingStateService,
    ) {
        effect(() => {
            const user = this._bookingStateService.user();

            if (user) {
                this.loadRecentlyPlayedWith();
            }
        });
    }

    ngOnInit(): void {
        this._searchTerms
            .pipe(
                tap(() => this.isHandlingSearch = true),
                debounceTime(750),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((term: string) => {
                this.performSearch(term);
                this.isHandlingSearch = false;
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    onSearchChange(searchTerm: string): void {
        this.searchTerm = searchTerm;
        this._searchTerms.next(searchTerm);
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.searchResults = [];
        this._searchTerms.next('');
    }

    private performSearch(term: string): void {
        if (!term || term.length < 2) {
            this.searchResults = [];
            return;
        }

        this.isLoading = true;

        const filter: UsersFilter = {
            pageNumber: 1,
            pageSize: 20,
            sortBy: 'FirstName',
            sortDirection: 'asc',
            search: term,
            isAdmin: false
        };

        this._usersService.getUsers(filter)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (response) => {
                    this.searchResults = response.items;
                },
            });
    }

    private loadRecentlyPlayedWith(): void {
        this.isLoading = true;
        this._usersService.getRecentlyPlayedWith()
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (users) => {
                    this.recentlyPlayedWith = users;                    
                },
                error: () => {
                }
            });
    }

    selectUser(user: ApplicationUser | RecentlyPlayedWithUser): void {
        this.pendingSelectedUser = user;
        const newSlot: PlayerSlot = {
            id: `user-${Date.now()}`,
            userId: user?.id || null,
            displayName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '',
            profileImageUrl: user?.profileImageUrl,
            cellphoneNumber: user?.phoneNumber,
            isCurrentUser: false,
            isGuest: false,
            isMember: false,
            isAffiliated: false,
            price: null,
            members: user.memberships,
            type: 'OTHER_USER',
            playerType: null,
        };

        this._bookingStateService.slots.update(slots => {
            const selectedIndex = this._bookingStateService.selectedPlayerSlot().index;

            slots[selectedIndex] = newSlot;
            const updated = [...slots];
            return updated;
        });

        this._bookingStateService.selectedPlayerSlot.update(slot => {
            return {
                ...newSlot,
                index: slot.index,
            }
        });

        const addPlayerDrawer = this._fuseDrawerService.getComponent('addPlayerDrawer');

        if (addPlayerDrawer) {
            addPlayerDrawer.close();
        }

        const playerTypeDrawer = this._fuseDrawerService.getComponent('playerTypeDrawer');
        if (playerTypeDrawer) {
            playerTypeDrawer.open();
        }
    }

    addGuest(): void {
        const newSlot: PlayerSlot = {
            id: `user-${Date.now()}`,
            displayName: 'Guest Player',
            isCurrentUser: false,
            isGuest: true,
            isMember: false,
            isAffiliated: false,
            price: null,
            members: [],
            type: 'GUEST',
            playerType: null,
        };

        this._bookingStateService.slots.update(slots => {
            const selectedIndex = this._bookingStateService.selectedPlayerSlot().index;

            slots[selectedIndex] = newSlot;
            const updated = [...slots];
            return updated;
        });

        this._bookingStateService.selectedPlayerSlot.update(slot => {
            return {
                ...newSlot,
                index: slot.index,
            }
        });

        const addPlayerDrawer = this._fuseDrawerService.getComponent('addPlayerDrawer');
        if (addPlayerDrawer) {
            addPlayerDrawer.close();
        }

        const playerTypeDrawer = this._fuseDrawerService.getComponent('playerTypeDrawer');

        if (playerTypeDrawer) {
            playerTypeDrawer.open();
        }
    }

    getDisplayedUsers(): (ApplicationUser | RecentlyPlayedWithUser)[] {
        if (this.searchTerm && this.searchTerm.length >= 2) {
            return this.searchResults;
        }

        return this.recentlyPlayedWith;
    }

    getListTitle(): string {
        if (this.searchTerm && this.searchTerm.length >= 2) {
            return 'Search Results';
        }
        return 'Recently Played With';
    }

    getUserName(user: ApplicationUser | RecentlyPlayedWithUser): string {
        return `${user.firstName} ${user.lastName}`.trim();
    }

    getUserImage(user: ApplicationUser | RecentlyPlayedWithUser): string | null {
        return user.profileImageUrl || null;
    }

    getUserInitials(user: ApplicationUser | RecentlyPlayedWithUser): string {
        const firstName = user.firstName?.charAt(0)?.toUpperCase() || '';
        const lastName = user.lastName?.charAt(0)?.toUpperCase() || '';
        return firstName + lastName || '?';
    }
}